import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Plus,
  Search,
  LayoutGrid,
  Columns,
  Settings,
  Users,
  TrendingUp,
  Calendar,
  Sparkles,
  Filter,
  Tags,
  X,
  List,
  CheckSquare,
  Eye,
  FileSpreadsheet,
  Download,
  Trash2,
  Pencil,
  Check,
  Building2,
  GitBranch,
  ArrowUpDown,
  BarChart3,
} from "lucide-react";
import { Collapsible, CollapsibleContent } from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import { TAG_COLORS } from "@/types/lead";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/hooks/useAuth";
import { useWorkspace } from "@/hooks/useWorkspace";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { LeadDialog } from "@/components/leads/LeadDialog";
import { ExportLeadsDialog } from "@/components/leads/ExportLeadsDialog";
import { LeadsKanban } from "@/components/leads/LeadsKanban";
import { LeadDetailDrawer } from "@/components/leads/LeadDetailDrawer";
import { LeadQuickChatSheet } from "@/components/leads/LeadQuickChatSheet";
import { DealDialog } from "@/components/deals/DealDialog";
import { useDealStages } from "@/hooks/useDealStages";
import { StageManagerDialog } from "@/components/leads/StageManagerDialog";
import { LeadCsvImportDialog } from "@/components/leads/LeadCsvImportDialog";
import { FunnelDropdownMenu } from "@/components/leads/FunnelDropdownMenu";
import { FunnelManagerDialog } from "@/components/leads/FunnelManagerDialog";
import { MoveLeadsDialog } from "@/components/leads/MoveLeadsDialog";
import { ContactGroupsManager } from "@/components/leads/ContactGroupsManager";
import { GroupDropdownMenu } from "@/components/leads/GroupDropdownMenu";
import { GroupStageManagerDialog } from "@/components/leads/GroupStageManagerDialog";
import { GroupsKanban } from "@/components/leads/GroupsKanban";
import { BulkActionsBar } from "@/components/leads/BulkActionsBar";
import { useSalesFunnels } from "@/hooks/useSalesFunnels";
import { useWorkspaceIntegrationsMap } from "@/hooks/useWorkspaceIntegrationsMap";
import { useMemberPermissions } from "@/hooks/useMemberPermissions";
import { useContactGroups } from "@/hooks/useContactGroups";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { PaywallOverlay } from "@/components/layout/PaywallOverlay";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import type { Lead, Stage, LeadTag } from "@/types/lead";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  buildCrmStatsContextKey,
  fetchCrmLeadStats,
  type CrmLeadStatsResult,
} from "@/lib/crm-lead-stats";
import {
  mergeLeadSnapshots,
  dedupeLeadsById,
  leadMatchesGlobalSearch,
  dedupeSearchPreviewByPhone,
  prepareLeadSearchTerm,
  sortLeadsBySearchRelevance,
} from "@/lib/leads-kanban-utils";
import { fetchLeadAssigneeInfoByLeadIds } from "@/lib/lead-member-assignments";
import { useUazapiContactsSyncWatcher } from "@/hooks/useUazapiContactsSyncWatcher";
import {
  effectiveConversationUnreadCount,
  loadConversationReadTimestamps,
} from "@/lib/conversation-list-map";
import {
  isRealtimePausedByVisibility,
  subscribeVisibilityPause,
} from "@/lib/realtime-visibility-pause";
import { REALTIME_PAUSE_ON_HIDDEN_ENABLED } from "@/lib/workspace-conversation-realtime-hub";
import { useWorkspaceConversationRealtime } from "@/hooks/useWorkspaceConversationRealtime";
import type { ConversationChangeHandler } from "@/lib/workspace-conversation-realtime-hub";

const LEADS_PER_PAGE = 100;
const LEADS_SERVER_PAGE_SIZE = 200;
const MAX_LOADED_LEADS = 5000;

export default function Leads() {
  const { user } = useAuth();
  const { workspaceId } = useWorkspace(user?.id);
  useUazapiContactsSyncWatcher(workspaceId);
  const { canManageFunnels, memberId } = useMemberPermissions();
  const { toast } = useToast();
  const { shouldShowPaywall } = useSubscription();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [filteredLeads, setFilteredLeads] = useState<Lead[]>([]);
  // Enrichment data fetched in batch (eliminates N+1 queries from LeadCard)
  const [primaryGroupsMap, setPrimaryGroupsMap] = useState<Map<string, { name: string; role?: string }>>(new Map());
  const [assignedMembersMap, setAssignedMembersMap] = useState<Map<string, string>>(new Map());
  const [closedLeadsSet, setClosedLeadsSet] = useState<Set<string>>(new Set());
  const [unreadCountsMap, setUnreadCountsMap] = useState<Map<string, number>>(new Map());
  const [unreadLastMessageAtMap, setUnreadLastMessageAtMap] = useState<Map<string, string>>(new Map());
  const realtimeChannelRef = useRef<any>(null);
  const tagsChannelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const loadedLeadIdsRef = useRef<Set<string>>(new Set());
  const selectedTagsRef = useRef<string[]>([]);
  const fetchLeadsRef = useRef<(silent?: boolean, options?: { loadMore?: boolean }) => Promise<void>>(async () => {});
  const fetchKanbanLeadsRef = useRef<(silent?: boolean) => Promise<void>>(async () => {});
  const viewModeRef = useRef<"grid" | "kanban" | "list">("kanban");
  const reconcileTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconcileFromBackgroundRef = useRef<() => void>(() => {});
  const realtimeRefetchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const debouncedEnrichmentRef = useRef<(leadIds: string[]) => void>(() => {});
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [selectedStage, setSelectedStage] = useState<string>("all");
  const [selectedGroupId, setSelectedGroupId] = useState<string>("all");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDetailDrawerOpen, setIsDetailDrawerOpen] = useState(false);
  const [isStageManagerOpen, setIsStageManagerOpen] = useState(false);
  const [isCsvImportOpen, setIsCsvImportOpen] = useState(false);
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const [isFunnelManagerOpen, setIsFunnelManagerOpen] = useState(false);
  const [isMoveLeadsOpen, setIsMoveLeadsOpen] = useState(false);
  const [isGroupsManagerOpen, setIsGroupsManagerOpen] = useState(false);
  const [isGroupStageManagerOpen, setIsGroupStageManagerOpen] = useState(false);
  const [isDealDialogOpen, setIsDealDialogOpen] = useState(false);
  const [newLeadForDeal, setNewLeadForDeal] = useState<{ id: string; name: string } | null>(null);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [quickChatLead, setQuickChatLead] = useState<Lead | null>(null);
  const [scrollToStageSlug, setScrollToStageSlug] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [viewMode, setViewMode] = useState<"grid" | "kanban" | "list">("kanban");
  const [dataMode, setDataMode] = useState<"funnels" | "groups">("funnels");
  const [selectedGroupIdForView, setSelectedGroupIdForView] = useState<string | null>(null);
  const [stages, setStages] = useState<Stage[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [workspaceTags, setWorkspaceTags] = useState<LeadTag[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [tagFilterOpen, setTagFilterOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [statsOpen, setStatsOpen] = useState(false);
  const [crmStats, setCrmStats] = useState<CrmLeadStatsResult | null>(null);
  const [crmStatsLoading, setCrmStatsLoading] = useState(false);
  const crmStatsLoadedKeyRef = useRef<string | null>(null);
  const crmStatsReqSeq = useRef(0);
  // Inline tag creation / edit states
  const [isCreatingTag, setIsCreatingTag] = useState(false);
  const [newTagName, setNewTagName] = useState("");
  const [newTagColor, setNewTagColor] = useState(TAG_COLORS[0].value);
  const [editingTag, setEditingTag] = useState<LeadTag | null>(null);
  const [sortOption, setSortOption] = useState<string>("recent");
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedLeadIds, setSelectedLeadIds] = useState<Set<string>>(new Set());
  const [fullySelectedStageSlugs, setFullySelectedStageSlugs] = useState<Set<string>>(new Set());
  const [selectingStageSlug, setSelectingStageSlug] = useState<string | null>(null);
  const [serverHasMore, setServerHasMore] = useState(false);
  const [serverLoadingMore, setServerLoadingMore] = useState(false);
  const [leadsCursor, setLeadsCursor] = useState<{ created_at: string; id: string } | null>(null);
  const [integrationCursor, setIntegrationCursor] = useState<{ order_index: number; lead_id: string } | null>(null);
  const [kanbanStageCounts, setKanbanStageCounts] = useState<Record<string, number>>({});
  const fetchSeqRef = useRef(0);
  const filterDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Autocomplete (mini-resultados) abaixo do campo de busca
  const [searchPreviewLeads, setSearchPreviewLeads] = useState<Lead[]>([]);
  const [searchPreviewLoading, setSearchPreviewLoading] = useState(false);
  const [searchDropdownOpen, setSearchDropdownOpen] = useState(false);
  const searchPreviewDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchDropdownRef = useRef<HTMLDivElement | null>(null);

  const toggleSelectLead = (id: string) => {
    const leadStage = leads.find((l) => l.id === id)?.stage;
    setSelectedLeadIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    if (leadStage) {
      setFullySelectedStageSlugs((prev) => {
        if (!prev.has(leadStage)) return prev;
        const next = new Set(prev);
        next.delete(leadStage);
        return next;
      });
    }
  };

  const toggleSelectAll = () => {
    if (selectedLeadIds.size === filteredLeads.length) {
      setSelectedLeadIds(new Set());
    } else {
      setSelectedLeadIds(new Set(filteredLeads.map((l) => l.id)));
    }
  };

  const toggleSelectLeads = (ids: string[]) => {
    if (ids.length === 0) return;
    setSelectedLeadIds((prev) => {
      const next = new Set(prev);
      const allSelected = ids.every((id) => next.has(id));
      if (allSelected) {
        ids.forEach((id) => next.delete(id));
      } else {
        ids.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const exitSelectionMode = () => {
    setIsSelectionMode(false);
    setSelectedLeadIds(new Set());
    setFullySelectedStageSlugs(new Set());
    setSelectingStageSlug(null);
  };

  const handleBulkComplete = () => {
    exitSelectionMode();
    if (viewMode === "kanban") {
      fetchKanbanLeads(true);
    } else {
      fetchLeads();
    }
    fetchWorkspaceTags();
  };

  // Hooks for funnels and groups
  const {
    funnels,
    loading: funnelsLoading,
    activeFunnelId,
    setActiveFunnelId,
    createFunnel,
    updateFunnel,
    deleteFunnel,
    setDefaultFunnel,
    reorderFunnels,
  } = useSalesFunnels(workspaceId);

  const integrationById = useWorkspaceIntegrationsMap(workspaceId);

  const { groups } = useContactGroups(workspaceId);
  const { stages: dealStages } = useDealStages(workspaceId);

  // Funis não-padrão leem a posição do lead pela participação no funil (lead pode estar em vários funis,
  // cada um com sua coluna). O funil padrão mantém a leitura legada (inclui leads sem funil).
  const activeFunnel = funnels.find(f => f.id === activeFunnelId);
  const isDefaultFunnel = !!activeFunnel?.is_default;
  const isIntegrationFunnel = !!activeFunnel && (!!activeFunnel.integration_id || !isDefaultFunnel);

  useEffect(() => {
    const funnelId = searchParams.get("funnel_id");
    const stage = searchParams.get("stage");
    if (!funnelId && !stage) return;

    if (funnelId) setActiveFunnelId(funnelId);
    if (stage) setScrollToStageSlug(stage);
    setDataMode("funnels");
    setViewMode("kanban");
    setSearchParams({}, { replace: true });
  }, [searchParams, setActiveFunnelId, setSearchParams]);

  useEffect(() => {
    crmStatsLoadedKeyRef.current = null;
    setCrmStats(null);
  }, [workspaceId, dataMode, activeFunnelId, selectedGroupIdForView, isIntegrationFunnel, isDefaultFunnel]);

  const requestCrmStats = useCallback(async () => {
    if (!workspaceId) return;

    const params = {
      workspaceId,
      mode: dataMode,
      funnelId: dataMode === "funnels" ? activeFunnelId : null,
      isIntegrationFunnel: dataMode === "funnels" ? isIntegrationFunnel : false,
      includeLegacyNullFunnel: dataMode === "funnels" ? Boolean(activeFunnelId && isDefaultFunnel) : false,
      groupId: dataMode === "groups" ? selectedGroupIdForView : null,
    } as const;

    const key = buildCrmStatsContextKey(params);
    if (crmStatsLoadedKeyRef.current === key) return;

    const seq = ++crmStatsReqSeq.current;
    setCrmStatsLoading(true);
    try {
      const result = await fetchCrmLeadStats(supabase, params);
      if (seq !== crmStatsReqSeq.current) return;
      setCrmStats(result);
      crmStatsLoadedKeyRef.current = key;
    } catch (e: any) {
      if (seq === crmStatsReqSeq.current) {
        crmStatsLoadedKeyRef.current = null;
        setCrmStats(null);
        toast({
          title: "Erro ao carregar estatísticas",
          description: e?.message || "Não foi possível buscar os dados.",
          variant: "destructive",
        });
      }
    } finally {
      if (seq === crmStatsReqSeq.current) {
        setCrmStatsLoading(false);
      }
    }
  }, [
    workspaceId,
    dataMode,
    activeFunnelId,
    selectedGroupIdForView,
    isIntegrationFunnel,
    isDefaultFunnel,
    toast,
  ]);

  // Debounce de busca para evitar “fetch a cada tecla”
  useEffect(() => {
    if (filterDebounceRef.current) {
      clearTimeout(filterDebounceRef.current);
    }
    filterDebounceRef.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim());
    }, 350);
    return () => {
      if (filterDebounceRef.current) clearTimeout(filterDebounceRef.current);
    };
  }, [searchTerm]);

  const SEARCH_PREVIEW_LIMIT = 50;
  const fetchSearchPreview = useCallback(
    async (term: string) => {
      const trimmed = term.trim();
      if (!workspaceId) return;
      if (!trimmed) {
        setSearchPreviewLeads([]);
        setSearchDropdownOpen(false);
        return;
      }
      setSearchPreviewLoading(true);
      setSearchDropdownOpen(true);
      try {
        const searchQuery = prepareLeadSearchTerm(trimmed);
        if (!searchQuery) {
          setSearchPreviewLeads([]);
          setSearchDropdownOpen(false);
          return;
        }

        const { data, error } = await supabase.rpc("search_leads_by_term", {
          p_workspace_id: workspaceId,
          p_term: searchQuery,
          p_limit: SEARCH_PREVIEW_LIMIT,
          p_funnel_id: null,
          p_include_null_funnel: false,
        });

        if (error) throw error;
        setSearchPreviewLeads(
          sortLeadsBySearchRelevance(
            dedupeSearchPreviewByPhone((data as Lead[]) || []),
            trimmed,
          ),
        );
      } catch {
        setSearchPreviewLeads([]);
      } finally {
        setSearchPreviewLoading(false);
      }
    },
    [workspaceId],
  );

  // Debounce do autocomplete (mini-resultados) — 300 ms
  useEffect(() => {
    if (searchPreviewDebounceRef.current) clearTimeout(searchPreviewDebounceRef.current);
    const term = searchTerm.trim();
    if (!term) {
      setSearchPreviewLeads([]);
      setSearchDropdownOpen(false);
      return () => {
        if (searchPreviewDebounceRef.current) clearTimeout(searchPreviewDebounceRef.current);
      };
    }
    searchPreviewDebounceRef.current = setTimeout(() => {
      fetchSearchPreview(term);
    }, 300);
    return () => {
      if (searchPreviewDebounceRef.current) clearTimeout(searchPreviewDebounceRef.current);
    };
  }, [searchTerm, fetchSearchPreview]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (searchDropdownOpen && searchDropdownRef.current && !searchDropdownRef.current.contains(e.target as Node)) {
        setSearchDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [searchDropdownOpen]);

  useEffect(() => {
    if (workspaceId) {
      setLeads([]);
      setFilteredLeads([]);
      setPrimaryGroupsMap(new Map());
      setClosedLeadsSet(new Set());
      setLeadsCursor(null);
      setIntegrationCursor(null);
      setServerHasMore(false);
      setCurrentPage(1);

      fetchStages();
      fetchWorkspaceTags();
    }
    return () => {
      if (realtimeChannelRef.current) {
        supabase.removeChannel(realtimeChannelRef.current);
        realtimeChannelRef.current = null;
      }
      if (tagsChannelRef.current) {
        supabase.removeChannel(tagsChannelRef.current);
        tagsChannelRef.current = null;
      }
    };
  }, [workspaceId]);

  // Re-fetch stages when funnel changes
  useEffect(() => {
    if (workspaceId) fetchStages();
  }, [activeFunnelId]);

  // Kanban: ordenação é local no LeadsKanban — não refazer fetch ao mudar só sortOption (evita I/O inútil).
  useEffect(() => {
    if (!workspaceId || dataMode !== "funnels" || viewMode !== "kanban") return;
    fetchKanbanLeads(true);
  }, [
    workspaceId,
    dataMode,
    activeFunnelId,
    isIntegrationFunnel,
    selectedStage,
    selectedTags.join(","),
    viewMode,
  ]);

  // Grade/lista: sort entra no RPC / fetchLeads.
  useEffect(() => {
    if (!workspaceId || dataMode !== "funnels" || viewMode === "kanban") return;
    fetchLeads(true);
  }, [
    workspaceId,
    dataMode,
    activeFunnelId,
    isIntegrationFunnel,
    debouncedSearchTerm,
    selectedStage,
    selectedTags.join(","),
    sortOption,
    viewMode,
  ]);

  // Filtro local: busca global + ordenação (sem refetch do Kanban a cada tecla).
  useEffect(() => {
    let base = leads;
    if (debouncedSearchTerm.trim()) {
      base = sortLeadsBySearchRelevance(
        dedupeSearchPreviewByPhone(
          leads.filter((l) => leadMatchesGlobalSearch(l, debouncedSearchTerm)),
        ),
        debouncedSearchTerm,
      );
    }

    if (sortOption !== "stage") {
      setFilteredLeads(base);
      setCurrentPage(1);
      return;
    }

    const sorted = [...base].sort((a, b) => {
      const stageA = stages.findIndex((s) => s.slug === a.stage);
      const stageB = stages.findIndex((s) => s.slug === b.stage);
      return stageA - stageB;
    });
    setFilteredLeads(sorted);
    setCurrentPage(1);
  }, [leads, sortOption, stages, debouncedSearchTerm]);

  const hasActiveListFilters = useMemo(
    () =>
      Boolean(
        debouncedSearchTerm.trim() || selectedStage !== "all" || selectedTags.length > 0,
      ),
    [debouncedSearchTerm, selectedStage, selectedTags.length],
  );

  const needsListPaginationTotal = viewMode !== "kanban" && !hasActiveListFilters;

  useEffect(() => {
    if (!workspaceId) return;
    if (statsOpen || needsListPaginationTotal) {
      void requestCrmStats();
    }
  }, [
    workspaceId,
    statsOpen,
    needsListPaginationTotal,
    dataMode,
    activeFunnelId,
    selectedGroupIdForView,
    isIntegrationFunnel,
    isDefaultFunnel,
    requestCrmStats,
  ]);

  const kanbanTotalFromCounts = useMemo(
    () => Object.values(kanbanStageCounts).reduce((sum, count) => sum + count, 0),
    [kanbanStageCounts],
  );

  /** Total alinhado ao card de estatísticas (RPC) ou ao filtro local quando há busca/estágio/tags. */
  const displayLeadTotal = useMemo(() => {
    if (hasActiveListFilters) return filteredLeads.length;
    if (viewMode === "kanban") return kanbanTotalFromCounts;
    const serverTotal = crmStats?.totalCount;
    if (serverTotal != null && serverTotal >= 0) return serverTotal;
    return filteredLeads.length;
  }, [
    hasActiveListFilters,
    filteredLeads.length,
    viewMode,
    kanbanTotalFromCounts,
    crmStats?.totalCount,
  ]);

  const kanbanDisplayCounts = useMemo(() => {
    if (!debouncedSearchTerm.trim()) return kanbanStageCounts;
    const counts: Record<string, number> = {};
    for (const lead of filteredLeads) {
      const slug = String(lead.stage || "");
      counts[slug] = (counts[slug] || 0) + 1;
    }
    return counts;
  }, [kanbanStageCounts, filteredLeads, debouncedSearchTerm]);

  const fetchLeadTagsForLeadIds = useCallback(async (leadIds: string[]) => {
    if (leadIds.length === 0) return new Map<string, LeadTag[]>();

    const chunkSize = 500;
    const tagsByLead = new Map<string, LeadTag[]>();

    for (let i = 0; i < leadIds.length; i += chunkSize) {
      const chunk = leadIds.slice(i, i + chunkSize);
      const { data, error } = await supabase
        .from("lead_tag_assignments")
        .select("lead_id, tag:tag_id (id, name, color)")
        .in("lead_id", chunk);

      if (error) throw error;

      for (const row of (data as any[]) || []) {
        const leadId = row.lead_id as string | null;
        const tag = row.tag as LeadTag | null;
        if (!leadId || !tag) continue;
        if (!tagsByLead.has(leadId)) tagsByLead.set(leadId, []);
        tagsByLead.get(leadId)!.push(tag);
      }
    }

    return tagsByLead;
  }, []);

  const fetchLeads = async (silent: boolean = false, options: { loadMore?: boolean } = {}) => {
    if (!workspaceId) return;
    if (dataMode !== "funnels") return;

    const loadMore = options.loadMore === true;

    if (loadMore) {
      if (serverLoadingMore || !serverHasMore) return;
      if (leads.length >= MAX_LOADED_LEADS) {
        setServerHasMore(false);
        toast({
          title: "Muitos leads carregados",
          description: `Para evitar travamentos, limite de ${MAX_LOADED_LEADS} leads por sessão. Use filtros para refinar.`,
          variant: "destructive",
        });
        return;
      }
    } else {
      setLeadsCursor(null);
      setIntegrationCursor(null);
      setServerHasMore(false);
      setPrimaryGroupsMap(new Map());
      setClosedLeadsSet(new Set());
      setCurrentPage(1);
      setIsSelectionMode(false);
      setSelectedLeadIds(new Set());
      setFullySelectedStageSlugs(new Set());
      setSelectingStageSlug(null);
    }

    const seq = ++fetchSeqRef.current;

    try {
      if (loadMore) setServerLoadingMore(true);
      else if (!silent) setInitialLoading(true);

      const limitPlusOne = LEADS_SERVER_PAGE_SIZE + 1;
      const funnelIdToFilter = activeFunnelId || null;

      let pageLeads: any[] = [];
      let nextHasMore = false;
      let nextLeadsCursor = leadsCursor;
      let nextIntegrationCursor = integrationCursor;

      if (isIntegrationFunnel && activeFunnelId) {
        // Integration funnels: paginar via lead_funnel_memberships (evita carregar todos os leads do workspace)
        let membershipQuery = supabase
          .from("lead_funnel_memberships")
          .select("lead_id, stage, order_index")
          .eq("funnel_id", activeFunnelId)
          .order("order_index", { ascending: true })
          .order("lead_id", { ascending: true })
          .limit(limitPlusOne);

        if (loadMore && integrationCursor) {
          membershipQuery = membershipQuery.or(
            `order_index.gt.${integrationCursor.order_index},and(order_index.eq.${integrationCursor.order_index},lead_id.gt.${integrationCursor.lead_id})`,
          );
        }

        const { data: memberships, error: membershipsError } = await membershipQuery;
        if (membershipsError) throw membershipsError;

        const slice = (memberships || []).slice(0, LEADS_SERVER_PAGE_SIZE);
        nextHasMore = (memberships || []).length > LEADS_SERVER_PAGE_SIZE;

        if (slice.length > 0) {
          const last = slice[slice.length - 1] as any;
          nextIntegrationCursor = {
            order_index: Number(last.order_index ?? 0),
            lead_id: String(last.lead_id),
          };
        }

        const leadIds = slice.map((m: any) => String(m.lead_id)).filter(Boolean);
        if (leadIds.length > 0) {
          let leadsQuery = supabase
            .from("leads")
            .select(
              "id, name, email, phone, stage, notes, assigned_agent_id, assigned_member_id, created_at, updated_at, current_appointment_id, current_appointment_date, order_index, metadata, funnel_id",
            )
            .eq("workspace_id", workspaceId)
            .in("id", leadIds);

          if (debouncedSearchTerm) {
            const escaped = debouncedSearchTerm.split(",").join(" ");
            leadsQuery = leadsQuery.or(
              `name.ilike.%${escaped}%,email.ilike.%${escaped}%,phone.ilike.%${escaped}%`,
            );
          }

          const { data: leadRows, error: leadRowsError } = await leadsQuery;
          if (leadRowsError) throw leadRowsError;

          const membershipByLeadId = new Map<string, any>();
          for (const m of slice as any[]) {
            membershipByLeadId.set(String(m.lead_id), m);
          }

          pageLeads = (leadRows || []).map((row: any) => {
            const membership = membershipByLeadId.get(String(row.id));
            return {
              ...row,
              stage: membership?.stage || row.stage,
              order_index: membership?.order_index ?? row.order_index ?? 0,
            };
          });

          if (selectedStage !== "all") {
            pageLeads = pageLeads.filter((l) => l.stage === selectedStage);
          }

          pageLeads.sort((a, b) => (a.order_index || 0) - (b.order_index || 0));
        }
      } else {
        // Standard funnels: keyset pagination por created_at/id via RPC (suporta filtro por tags)
        const rpcArgs = {
          p_workspace_id: workspaceId,
          p_search: debouncedSearchTerm || null,
          p_stage: selectedStage === "all" ? null : selectedStage,
          p_tag_ids: selectedTags.length > 0 ? selectedTags : null,
          p_sort: sortOption,
          p_limit: limitPlusOne,
          p_cursor_created_at: loadMore ? leadsCursor?.created_at ?? null : null,
          p_cursor_id: loadMore ? leadsCursor?.id ?? null : null,
          p_funnel_id: funnelIdToFilter,
        };

        const includeLegacyNullFunnel = Boolean(funnelIdToFilter && isDefaultFunnel);

        // Enquanto existirem leads com funnel_id NULL (legado), o funil padrão precisa incluí-los.
        // Como o RPC pode não existir/estar desatualizado, priorizamos um fallback consistente no funil padrão.
        const shouldSkipRpc = includeLegacyNullFunnel;

        const { data: rpcData, error: rpcError } = shouldSkipRpc
          ? ({ data: null, error: { message: "skip rpc for default funnel legacy null funnel_id" } } as any)
          : await (supabase as any).rpc("search_leads_page", rpcArgs);

        if (rpcError) {
          // Fallback seguro: limita e evita loop/range sem fim (mantém app funcional antes do SQL ser aplicado)
          let q = supabase
            .from("leads")
            .select(
              "id, name, email, phone, stage, notes, assigned_agent_id, assigned_member_id, created_at, updated_at, current_appointment_id, current_appointment_date, order_index, metadata, funnel_id",
            )
            .eq("workspace_id", workspaceId)
            .order("created_at", { ascending: false })
            .order("id", { ascending: false })
            .limit(limitPlusOne);

          if (loadMore && leadsCursor) {
            q = q.lt("created_at", leadsCursor.created_at);
          }

          if (includeLegacyNullFunnel && funnelIdToFilter) {
            if (debouncedSearchTerm) {
              const escaped = debouncedSearchTerm.split(",").join(" ");
              q = q.or(
                `and(funnel_id.eq.${funnelIdToFilter},name.ilike.%${escaped}%),and(funnel_id.eq.${funnelIdToFilter},email.ilike.%${escaped}%),and(funnel_id.eq.${funnelIdToFilter},phone.ilike.%${escaped}%),and(funnel_id.is.null,name.ilike.%${escaped}%),and(funnel_id.is.null,email.ilike.%${escaped}%),and(funnel_id.is.null,phone.ilike.%${escaped}%)`,
              );
            } else {
              q = q.or(`funnel_id.eq.${funnelIdToFilter},funnel_id.is.null`);
            }
          } else {
            if (funnelIdToFilter) q = q.eq("funnel_id", funnelIdToFilter);
            if (debouncedSearchTerm) {
              const escaped = debouncedSearchTerm.split(",").join(" ");
              q = q.or(`name.ilike.%${escaped}%,email.ilike.%${escaped}%,phone.ilike.%${escaped}%`);
            }
          }

          if (selectedStage !== "all") q = q.eq("stage", selectedStage as any);

          const { data: fallbackData, error: fallbackError } = await q;
          if (fallbackError) throw fallbackError;
          pageLeads = fallbackData || [];
        } else {
          pageLeads = rpcData || [];
        }

        nextHasMore = pageLeads.length > LEADS_SERVER_PAGE_SIZE;
        pageLeads = pageLeads.slice(0, LEADS_SERVER_PAGE_SIZE);

        if (pageLeads.length > 0) {
          const last = pageLeads[pageLeads.length - 1] as any;
          nextLeadsCursor = { created_at: String(last.created_at), id: String(last.id) };
        }
      }

      // Tags em batch (projeção mínima na query principal)
      const pageLeadIds = pageLeads.map((l: any) => String(l.id)).filter(Boolean);
      const tagsByLead = await fetchLeadTagsForLeadIds(pageLeadIds);
      let pageLeadsWithTags = pageLeads.map((lead: any) => ({
        ...lead,
        tags: tagsByLead.get(String(lead.id)) || [],
      }));

      if (selectedTags.length > 0) {
        pageLeadsWithTags = pageLeadsWithTags.filter((lead: any) => {
          const leadTagIds = new Set<string>(((lead.tags as LeadTag[]) || []).map((t) => t.id));
          return selectedTags.every((id) => leadTagIds.has(id));
        });
      }

      if (seq !== fetchSeqRef.current) return;

      setLeads((prev) => (loadMore ? [...prev, ...pageLeadsWithTags] : pageLeadsWithTags));
      setServerHasMore(nextHasMore);
      setLeadsCursor(nextLeadsCursor);
      setIntegrationCursor(nextIntegrationCursor);

      fetchEnrichmentData(pageLeadsWithTags.map((l: any) => String(l.id)).filter(Boolean));
    } catch (error: any) {
      toast({
        title: "Erro ao carregar leads",
        description: error.message || "Não foi possível carregar os leads.",
        variant: "destructive",
      });
    } finally {
      if (loadMore) setServerLoadingMore(false);
      if (!loadMore) setInitialLoading(false);
    }
  };


  useEffect(() => {
    loadedLeadIdsRef.current = new Set(leads.map((l) => l.id));
  }, [leads]);

  useEffect(() => {
    selectedTagsRef.current = selectedTags;
  }, [selectedTags]);

  useEffect(() => {
    fetchLeadsRef.current = fetchLeads;
  }, [fetchLeads]);

  const fetchWorkspaceTags = useCallback(async () => {
    if (!workspaceId) return;
    try {
      const { data, error } = await supabase
        .from("lead_tags")
        .select("id, name, color")
        .eq("workspace_id", workspaceId)
        .order("name");

      if (error) throw error;
      setWorkspaceTags(data || []);
    } catch (error) {
      console.error("Error fetching workspace tags:", error);
    }
  }, [workspaceId]);

  const patchLeadTagInList = useCallback((tagId: string, patch: Pick<LeadTag, "name" | "color">) => {
    setLeads((prev) =>
      prev.map((lead) => {
        if (!lead.tags?.some((t) => t.id === tagId)) return lead;
        return {
          ...lead,
          tags: lead.tags.map((t) => (t.id === tagId ? { ...t, ...patch } : t)),
        };
      }),
    );
  }, []);

  useEffect(() => {
    if (!workspaceId) return;

    const subscribeTagsChannel = () => {
      if (isRealtimePausedByVisibility(REALTIME_PAUSE_ON_HIDDEN_ENABLED)) return;
      if (tagsChannelRef.current) return;

      const channel = supabase
        .channel(`leads-page-tags-${workspaceId}`)
        .on(
          "postgres_changes",
          {
            event: "*",
            schema: "public",
            table: "lead_tags",
            filter: `workspace_id=eq.${workspaceId}`,
          },
          (payload) => {
            if (isRealtimePausedByVisibility(REALTIME_PAUSE_ON_HIDDEN_ENABLED)) return;
            if (payload.eventType === "UPDATE" && payload.new) {
              const updated = payload.new as { id: string; name: string; color: string };
              patchLeadTagInList(updated.id, { name: updated.name, color: updated.color });
            }
            fetchWorkspaceTags();
          },
        )
        .subscribe();

      tagsChannelRef.current = channel;
    };

    const teardownTagsChannel = () => {
      if (!tagsChannelRef.current) return;
      supabase.removeChannel(tagsChannelRef.current);
      tagsChannelRef.current = null;
    };

    subscribeTagsChannel();

    let tagsReconcileTimer: ReturnType<typeof setTimeout> | null = null;
    const debouncedFetchWorkspaceTags = () => {
      if (tagsReconcileTimer) clearTimeout(tagsReconcileTimer);
      tagsReconcileTimer = setTimeout(() => {
        fetchWorkspaceTags();
      }, 300);
    };

    const unsubscribeVisibilityPause = subscribeVisibilityPause(REALTIME_PAUSE_ON_HIDDEN_ENABLED, {
      onPause: teardownTagsChannel,
      onResume: () => {
        if (!tagsChannelRef.current) subscribeTagsChannel();
        if (REALTIME_PAUSE_ON_HIDDEN_ENABLED) {
          debouncedFetchWorkspaceTags();
        }
      },
    });

    return () => {
      if (tagsReconcileTimer) clearTimeout(tagsReconcileTimer);
      unsubscribeVisibilityPause();
      teardownTagsChannel();
    };
  }, [workspaceId, fetchWorkspaceTags, patchLeadTagInList]);

  // Batch fetch enrichment data for all leads (eliminates N+1 queries from LeadCard)
  const fetchEnrichmentData = useCallback(async (leadIds: string[]) => {
    if (leadIds.length === 0) return;
    if (isRealtimePausedByVisibility(REALTIME_PAUSE_ON_HIDDEN_ENABLED)) return;

    try {
      // Batch in chunks of 500 to avoid query limits
      const chunkSize = 500;
      const groupsUpdates = new Map<string, { name: string; role?: string } | null>();
      const assignmentUpdates = new Map<string, string | null>();
      const closedUpdates = new Map<string, boolean>();
      const unreadUpdates = new Map<string, number>();
      const unreadLastMessageAtUpdates = new Map<string, string>();
      const readTimestamps = workspaceId ? loadConversationReadTimestamps(workspaceId) : new Map();

      for (let i = 0; i < leadIds.length; i += chunkSize) {
        const chunk = leadIds.slice(i, i + chunkSize);

        const [groupRes, convRes, assigneeRes] = await Promise.all([
          supabase
            .from("lead_group_memberships")
            .select(`lead_id, role, group:group_id (name)`)
            .in("lead_id", chunk)
            .eq("is_primary", true),
          supabase
            .from("conversations")
            .select("id, lead_id, is_closed, channel, unread_count, last_message_at")
            .in("lead_id", chunk),
          fetchLeadAssigneeInfoByLeadIds(supabase, chunk, workspaceId!),
        ]);

        if (groupRes.error) throw groupRes.error;
        if (convRes.error) throw convRes.error;

        for (const leadId of chunk) {
          const info = assigneeRes.get(leadId);
          assignmentUpdates.set(leadId, info?.label ?? null);
        }

        const seenPrimaryGroup = new Set<string>();
        if (groupRes.data) {
          for (const item of groupRes.data) {
            if (item.group) {
              const leadId = String(item.lead_id);
              seenPrimaryGroup.add(leadId);
              groupsUpdates.set(leadId, {
                name: (item.group as any).name,
                role: item.role || undefined,
              });
            }
          }
        }

        for (const leadId of chunk) {
          if (!seenPrimaryGroup.has(leadId)) {
            groupsUpdates.set(leadId, null);
          }
        }

        const convByLead = new Map<string, boolean[]>();
        const channelByLead = new Map<string, string | null>();
        const unreadByLead = new Map<string, number>();
        const unreadLastMessageAtByLead = new Map<string, string>();
        if (convRes.data) {
          for (const conv of convRes.data) {
            if (!conv.lead_id) continue;
            const leadId = String(conv.lead_id);
            if (!convByLead.has(leadId)) convByLead.set(leadId, []);
            convByLead.get(leadId)!.push(Boolean(conv.is_closed));
            if (!channelByLead.has(leadId)) {
              channelByLead.set(leadId, (conv as any).channel ?? null);
            }
            const effectiveUnread = effectiveConversationUnreadCount(
              String(conv.id),
              typeof conv.unread_count === "number" ? conv.unread_count : 0,
              conv.last_message_at,
              readTimestamps,
            );
            if (effectiveUnread > 0) {
              unreadByLead.set(leadId, (unreadByLead.get(leadId) || 0) + effectiveUnread);
              const lastAt = conv.last_message_at;
              if (lastAt) {
                const prev = unreadLastMessageAtByLead.get(leadId);
                if (!prev || lastAt > prev) {
                  unreadLastMessageAtByLead.set(leadId, lastAt);
                }
              }
            }
          }
        }

        const channelUpdates = new Map<string, string | null>();
        for (const leadId of chunk) {
          const states = convByLead.get(leadId) || [];
          const allClosed = states.length > 0 && states.every(Boolean);
          closedUpdates.set(leadId, allClosed);
          channelUpdates.set(leadId, channelByLead.get(leadId) ?? null);
          unreadUpdates.set(leadId, unreadByLead.get(leadId) || 0);
          const lastAt = unreadLastMessageAtByLead.get(leadId);
          if (lastAt) unreadLastMessageAtUpdates.set(leadId, lastAt);
        }

        if (channelUpdates.size > 0) {
          setLeads((prev) =>
            prev.map((l) => (channelUpdates.has(l.id) ? { ...l, channel: channelUpdates.get(l.id) } : l)),
          );
        }
      }

      setPrimaryGroupsMap((prev) => {
        const next = new Map(prev);
        for (const [leadId, value] of groupsUpdates) {
          if (!value) next.delete(leadId);
          else next.set(leadId, value);
        }
        return next;
      });

      setAssignedMembersMap((prev) => {
        const next = new Map(prev);
        for (const [leadId, name] of assignmentUpdates) {
          if (!name) next.delete(leadId);
          else next.set(leadId, name);
        }
        return next;
      });

      setClosedLeadsSet((prev) => {
        const next = new Set(prev);
        for (const [leadId, allClosed] of closedUpdates) {
          if (allClosed) next.add(leadId);
          else next.delete(leadId);
        }
        return next;
      });

      setUnreadCountsMap((prev) => {
        const next = new Map(prev);
        for (const [leadId, count] of unreadUpdates) {
          if (count > 0) next.set(leadId, count);
          else next.delete(leadId);
        }
        return next;
      });

      setUnreadLastMessageAtMap((prev) => {
        const next = new Map(prev);
        for (const [leadId, count] of unreadUpdates) {
          if (count > 0) {
            const lastAt = unreadLastMessageAtUpdates.get(leadId);
            if (lastAt) next.set(leadId, lastAt);
          } else {
            next.delete(leadId);
          }
        }
        return next;
      });
    } catch (error) {
      console.error("Error fetching enrichment data:", error);
    }
  }, [workspaceId]);

  const fetchKanbanLeads = useCallback(async (silent: boolean = false) => {
    if (!workspaceId || dataMode !== "funnels") return;
    const seq = ++fetchSeqRef.current;
    try {
      if (!silent) setInitialLoading(true);
      const funnelIdToFilter = activeFunnelId || null;
      const includeLegacy = Boolean(funnelIdToFilter && isDefaultFunnel);

      const { data, error } = await (supabase as any).rpc("fetch_kanban_leads", {
        p_workspace_id: workspaceId,
        p_funnel_id: funnelIdToFilter,
        p_include_null_funnel: includeLegacy,
        p_per_stage: 50,
        p_is_integration_funnel: isIntegrationFunnel,
        p_search: null,
      });

      if (error) throw error;
      if (seq !== fetchSeqRef.current) return;

      const counts: Record<string, number> = data?.counts || {};
      const rawLeads: any[] = data?.leads || [];

      const leadIds = rawLeads.map((l: any) => String(l.id));
      const tagsByLead = await fetchLeadTagsForLeadIds(leadIds);
      let leadsWithTags = rawLeads.map((lead: any) => ({
        ...lead,
        tags: tagsByLead.get(String(lead.id)) || [],
      }));

      let effectiveCounts = counts;
      if (selectedTags.length > 0) {
        leadsWithTags = leadsWithTags.filter((lead: any) => {
          const leadTagIds = new Set<string>(((lead.tags as LeadTag[]) || []).map((t) => t.id));
          return selectedTags.every((id) => leadTagIds.has(id));
        });
        // Recalcular contadores das colunas a partir dos leads já filtrados
        const recomputed: Record<string, number> = {};
        for (const lead of leadsWithTags) {
          const slug = String((lead as any).stage || "");
          recomputed[slug] = (recomputed[slug] || 0) + 1;
        }
        effectiveCounts = recomputed;
      }

      if (seq !== fetchSeqRef.current) return;

      setKanbanStageCounts(effectiveCounts);
      setLeads((prev) => dedupeLeadsById(mergeLeadSnapshots(prev, leadsWithTags)));
      setServerHasMore(false);
      fetchEnrichmentData(leadsWithTags.map((l: any) => String(l.id)));
    } catch (error: any) {
      console.error("fetchKanbanLeads error:", error);
      fetchLeads(silent);
    } finally {
      setInitialLoading(false);
    }
  }, [
    workspaceId,
    dataMode,
    activeFunnelId,
    isDefaultFunnel,
    isIntegrationFunnel,
    fetchLeadTagsForLeadIds,
    fetchEnrichmentData,
    selectedTags,
  ]);

  useEffect(() => {
    fetchKanbanLeadsRef.current = fetchKanbanLeads;
  }, [fetchKanbanLeads]);

  useEffect(() => {
    viewModeRef.current = viewMode;
  }, [viewMode]);

  const reconcileFromBackground = useCallback(() => {
    if (typeof document !== "undefined" && document.hidden) return;
    if (reconcileTimerRef.current) clearTimeout(reconcileTimerRef.current);
    reconcileTimerRef.current = setTimeout(() => {
      if (viewModeRef.current === "kanban") {
        fetchKanbanLeadsRef.current(true);
      } else {
        fetchLeadsRef.current(true);
      }
    }, 300);
  }, []);

  useEffect(() => {
    reconcileFromBackgroundRef.current = reconcileFromBackground;
  }, [reconcileFromBackground]);

  const handleLoadMoreKanbanStage = useCallback(async (stageSlug: string) => {
    if (!workspaceId) return;
    if (selectedTags.length > 0) return;
    const currentCount = leads.filter(l => l.stage === stageSlug).length;
    const funnelIdToFilter = activeFunnelId || null;
    const includeLegacy = Boolean(funnelIdToFilter && isDefaultFunnel);

    try {
      let newLeads: any[] = [];

      if (isIntegrationFunnel && funnelIdToFilter) {
        const loadedInStage = leads.filter((l) => l.stage === stageSlug);
        const lastLead = loadedInStage[loadedInStage.length - 1];
        let q = supabase
          .from("lead_funnel_memberships")
          .select(
            `lead_id, order_index, lead:lead_id (
              id, name, email, phone, stage, notes, assigned_agent_id, assigned_member_id,
              created_at, updated_at, current_appointment_id, current_appointment_date,
              order_index, metadata, funnel_id
            )`,
          )
          .eq("funnel_id", funnelIdToFilter)
          .eq("stage", stageSlug)
          .order("order_index", { ascending: true })
          .order("lead_id", { ascending: true })
          .limit(50);

        if (lastLead) {
          q = q.or(
            `order_index.gt.${lastLead.order_index ?? 0},and(order_index.eq.${lastLead.order_index ?? 0},lead_id.gt.${lastLead.id})`,
          );
        }

        const { data, error } = await q;
        if (error) throw error;
        newLeads = (data || [])
          .map((row: any) => row.lead)
          .filter(Boolean);
      } else {
        let q = supabase
          .from("leads")
          .select("id, name, email, phone, stage, notes, assigned_agent_id, assigned_member_id, created_at, updated_at, current_appointment_id, current_appointment_date, order_index, metadata, funnel_id")
          .eq("workspace_id", workspaceId)
          .eq("stage", stageSlug as any)
          .order("order_index", { ascending: true })
          .order("created_at", { ascending: false })
          .range(currentCount, currentCount + 49);

        if (includeLegacy && funnelIdToFilter) {
          q = q.or(`funnel_id.eq.${funnelIdToFilter},funnel_id.is.null`);
        } else if (funnelIdToFilter) {
          q = q.eq("funnel_id", funnelIdToFilter);
        }

        const { data, error } = await q;
        if (error) throw error;
        newLeads = data || [];
      }

      const newIds = newLeads.map((l: any) => String(l.id));
      const tagsByLead = await fetchLeadTagsForLeadIds(newIds);
      const newLeadsWithTags = newLeads.map((lead: any) => ({
        ...lead,
        tags: tagsByLead.get(String(lead.id)) || [],
      }));

      setLeads((prev) => dedupeLeadsById([...prev, ...newLeadsWithTags]));
      fetchEnrichmentData(newIds);
    } catch (error: any) {
      toast({
        title: "Erro ao carregar mais leads",
        description: error.message,
        variant: "destructive",
      });
    }
  }, [workspaceId, activeFunnelId, isDefaultFunnel, isIntegrationFunnel, leads, fetchLeadTagsForLeadIds, fetchEnrichmentData, toast, selectedTags]);

  const STAGE_IDS_PAGE_SIZE = 1000;

  const fetchAllLeadIdsInStage = useCallback(async (stageSlug: string): Promise<string[]> => {
    if (!workspaceId) return [];

    const funnelIdToFilter = activeFunnelId || null;
    const includeLegacy = Boolean(funnelIdToFilter && isDefaultFunnel);
    const allIds: string[] = [];

    if (isIntegrationFunnel && funnelIdToFilter) {
      let cursor: string | null = null;

      for (;;) {
        let q = supabase
          .from("lead_funnel_memberships")
          .select("lead_id")
          .eq("funnel_id", funnelIdToFilter)
          .eq("stage", stageSlug)
          .order("lead_id", { ascending: true })
          .limit(STAGE_IDS_PAGE_SIZE);

        if (cursor) {
          q = q.gt("lead_id", cursor);
        }

        const { data, error } = await q;
        if (error) throw error;

        const rows = data || [];
        if (rows.length === 0) break;

        allIds.push(...rows.map((r) => String(r.lead_id)));
        if (rows.length < STAGE_IDS_PAGE_SIZE) break;

        cursor = String(rows[rows.length - 1].lead_id);
      }

      return allIds;
    }

    let cursor: string | null = null;

    for (;;) {
      let q = supabase
        .from("leads")
        .select("id")
        .eq("workspace_id", workspaceId)
        .eq("stage", stageSlug as any)
        .order("id", { ascending: true })
        .limit(STAGE_IDS_PAGE_SIZE);

      if (includeLegacy && funnelIdToFilter) {
        q = q.or(`funnel_id.eq.${funnelIdToFilter},funnel_id.is.null`);
      } else if (funnelIdToFilter) {
        q = q.eq("funnel_id", funnelIdToFilter);
      }

      if (cursor) {
        q = q.gt("id", cursor);
      }

      const { data, error } = await q;
      if (error) throw error;

      const rows = data || [];
      if (rows.length === 0) break;

      allIds.push(...rows.map((r) => String(r.id)));
      if (rows.length < STAGE_IDS_PAGE_SIZE) break;

      cursor = String(rows[rows.length - 1].id);
    }

    return allIds;
  }, [workspaceId, activeFunnelId, isIntegrationFunnel, isDefaultFunnel]);

  const toggleSelectLeadsInStage = useCallback(
    async (
      stageSlug: string,
      visibleIds: string[],
      options: { totalCount: number; searchActive: boolean },
    ) => {
      const { searchActive } = options;

      if (searchActive || selectedTags.length > 0) {
        toggleSelectLeads(visibleIds);
        return;
      }

      setSelectingStageSlug(stageSlug);
      try {
        const targetIds = await fetchAllLeadIdsInStage(stageSlug);

        const allTargetSelected =
          fullySelectedStageSlugs.has(stageSlug) ||
          (targetIds.length > 0 && targetIds.every((id) => selectedLeadIds.has(id)));

        setSelectedLeadIds((prev) => {
          const next = new Set(prev);
          if (allTargetSelected) {
            targetIds.forEach((id) => next.delete(id));
          } else {
            targetIds.forEach((id) => next.add(id));
          }
          return next;
        });

        setFullySelectedStageSlugs((prev) => {
          const next = new Set(prev);
          if (allTargetSelected) next.delete(stageSlug);
          else next.add(stageSlug);
          return next;
        });
      } catch (error: any) {
        toast({
          title: "Erro ao selecionar leads",
          description: error.message,
          variant: "destructive",
        });
      } finally {
        setSelectingStageSlug(null);
      }
    },
    [fetchAllLeadIdsInStage, selectedTags, fullySelectedStageSlugs, selectedLeadIds, toast],
  );

  const handleLeadsWorkspaceConversationChange = useCallback<ConversationChangeHandler>(
    (payload) => {
      if (isRealtimePausedByVisibility(REALTIME_PAUSE_ON_HIDDEN_ENABLED)) return;
      const leadId: string | undefined =
        (payload.new as { lead_id?: string } | null)?.lead_id ??
        (payload.old as { lead_id?: string } | null)?.lead_id;
      if (!leadId || !loadedLeadIdsRef.current.has(leadId)) return;
      debouncedEnrichmentRef.current([leadId]);
    },
    [],
  );

  useWorkspaceConversationRealtime(
    workspaceId,
    handleLeadsWorkspaceConversationChange,
    Boolean(workspaceId),
  );

  // Setup consolidated realtime channel (1 canal), sem refetch total
  useEffect(() => {
    if (!workspaceId) return;

    const isPaused = () => isRealtimePausedByVisibility(REALTIME_PAUSE_ON_HIDDEN_ENABLED);
    let enrichmentDebounceTimer: ReturnType<typeof setTimeout> | null = null;
    const pendingEnrichmentLeadIds = new Set<string>();

    const debouncedFetchEnrichment = (leadIds: string[]) => {
      for (const id of leadIds) pendingEnrichmentLeadIds.add(id);
      if (enrichmentDebounceTimer) clearTimeout(enrichmentDebounceTimer);
      enrichmentDebounceTimer = setTimeout(() => {
        const ids = [...pendingEnrichmentLeadIds];
        pendingEnrichmentLeadIds.clear();
        if (ids.length > 0) void fetchEnrichmentData(ids);
      }, 300);
    };
    debouncedEnrichmentRef.current = debouncedFetchEnrichment;

    const clearEnrichmentDebounce = () => {
      if (enrichmentDebounceTimer) {
        clearTimeout(enrichmentDebounceTimer);
        enrichmentDebounceTimer = null;
      }
      pendingEnrichmentLeadIds.clear();
    };

    const subscribeEnrichmentChannel = () => {
      if (isPaused()) return;
      if (realtimeChannelRef.current) return;

      const channel = supabase
        .channel(`leads-enrichment-${workspaceId}`)
        .on("postgres_changes", { event: "*", schema: "public", table: "lead_tag_assignments" }, async (payload) => {
          if (isPaused()) return;
          const leadId: string | undefined =
            (payload.new as any)?.lead_id || (payload.old as any)?.lead_id || undefined;
          if (!leadId) return;

          if (selectedTagsRef.current.length > 0) {
            if (realtimeRefetchDebounceRef.current) clearTimeout(realtimeRefetchDebounceRef.current);
            realtimeRefetchDebounceRef.current = setTimeout(() => {
              fetchLeadsRef.current(true);
            }, 800);
            return;
          }

          if (!loadedLeadIdsRef.current.has(leadId)) return;

          try {
            const tagsByLead = await fetchLeadTagsForLeadIds([leadId]);
            const tags = tagsByLead.get(leadId) || [];
            setLeads((prev) => prev.map((l) => (l.id === leadId ? { ...l, tags } : l)));
          } catch (e) {
            console.error("Error patching lead tags:", e);
          }
        })
        .on("postgres_changes", { event: "*", schema: "public", table: "lead_group_memberships" }, (payload) => {
          if (isPaused()) return;
          const leadId: string | undefined =
            (payload.new as any)?.lead_id || (payload.old as any)?.lead_id || undefined;
          if (!leadId) return;
          if (!loadedLeadIdsRef.current.has(leadId)) return;
          debouncedFetchEnrichment([leadId]);
        })
        .on(
          "postgres_changes",
          {
            event: "*",
            schema: "public",
            table: "lead_member_assignments",
            filter: `workspace_id=eq.${workspaceId}`,
          },
          (payload) => {
            if (isPaused()) return;
            const leadId: string | undefined =
              (payload.new as { lead_id?: string } | null)?.lead_id ??
              (payload.old as { lead_id?: string } | null)?.lead_id;
            if (!leadId || !loadedLeadIdsRef.current.has(leadId)) return;
            debouncedFetchEnrichment([leadId]);
          },
        )
        .on(
          "postgres_changes",
          {
            event: "UPDATE",
            schema: "public",
            table: "leads",
            filter: `workspace_id=eq.${workspaceId}`,
          },
          (payload) => {
            if (isPaused()) return;
            const leadId = (payload.new as { id?: string })?.id;
            if (!leadId || !loadedLeadIdsRef.current.has(leadId)) return;

            const oldAssigned = (payload.old as { assigned_member_id?: string | null })?.assigned_member_id ?? null;
            const newAssigned = (payload.new as { assigned_member_id?: string | null })?.assigned_member_id ?? null;
            if (oldAssigned === newAssigned) return;

            setLeads((prev) =>
              prev.map((l) => (l.id === leadId ? { ...l, assigned_member_id: newAssigned ?? undefined } : l)),
            );
            debouncedFetchEnrichment([leadId]);
          },
        )
        .subscribe();

      realtimeChannelRef.current = channel;
    };

    const teardownEnrichmentChannel = () => {
      if (!realtimeChannelRef.current) return;
      supabase.removeChannel(realtimeChannelRef.current);
      realtimeChannelRef.current = null;
    };

    subscribeEnrichmentChannel();

    const unsubscribeVisibilityPause = subscribeVisibilityPause(REALTIME_PAUSE_ON_HIDDEN_ENABLED, {
      onPause: () => {
        clearEnrichmentDebounce();
        teardownEnrichmentChannel();
      },
      onResume: () => {
        if (!realtimeChannelRef.current) subscribeEnrichmentChannel();
        if (REALTIME_PAUSE_ON_HIDDEN_ENABLED) {
          reconcileFromBackgroundRef.current();
        }
      },
    });

    let removeVisibilityReconcile: (() => void) | undefined;
    if (!REALTIME_PAUSE_ON_HIDDEN_ENABLED && typeof document !== "undefined") {
      const onVisibilityChange = () => {
        if (!document.hidden) reconcileFromBackgroundRef.current();
      };
      document.addEventListener("visibilitychange", onVisibilityChange);
      removeVisibilityReconcile = () =>
        document.removeEventListener("visibilitychange", onVisibilityChange);
    }

    return () => {
      unsubscribeVisibilityPause();
      removeVisibilityReconcile?.();
      clearEnrichmentDebounce();
      teardownEnrichmentChannel();
      if (realtimeRefetchDebounceRef.current) {
        clearTimeout(realtimeRefetchDebounceRef.current);
        realtimeRefetchDebounceRef.current = null;
      }
      if (reconcileTimerRef.current) clearTimeout(reconcileTimerRef.current);
    };
  }, [workspaceId, fetchEnrichmentData, fetchLeadTagsForLeadIds]);

  useEffect(() => {
    if (!workspaceId) return;

    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    const refreshUnreadCounts = () => {
      if (isRealtimePausedByVisibility(REALTIME_PAUSE_ON_HIDDEN_ENABLED)) return;
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        const ids = [...loadedLeadIdsRef.current];
        if (ids.length > 0) fetchEnrichmentData(ids);
      }, 300);
    };

    window.addEventListener("conversation-read", refreshUnreadCounts);
    window.addEventListener("conversation-unread", refreshUnreadCounts);
    return () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      window.removeEventListener("conversation-read", refreshUnreadCounts);
      window.removeEventListener("conversation-unread", refreshUnreadCounts);
    };
  }, [workspaceId, fetchEnrichmentData]);

  const handleTagToggle = (tagId: string) => {
    setSelectedTags((prev) => (prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId]));
  };

  const clearTagFilter = () => {
    setSelectedTags([]);
  };

  const handleCreateTagInline = async () => {
    if (!newTagName.trim() || !workspaceId) return;

    setEditingTag(null);
    const trimmed = newTagName.trim();
    const existingTag = workspaceTags.find(
      (t) => t.name.toLowerCase() === trimmed.toLowerCase() && !t.id.startsWith("temp-"),
    );
    if (existingTag) {
      toast({
        title: "Esta tag já existe",
        description: "Selecione-a na lista.",
        variant: "destructive",
      });
      return;
    }

    const tempId = `temp-${crypto.randomUUID()}`;
    const optimistic: LeadTag = { id: tempId, name: trimmed, color: newTagColor };
    setWorkspaceTags((prev) =>
      [...prev, optimistic].sort((a, b) => a.name.localeCompare(b.name, "pt-BR")),
    );
    setNewTagName("");
    setNewTagColor(TAG_COLORS[0].value);
    setIsCreatingTag(false);

    try {
      const { data, error } = await supabase
        .from("lead_tags")
        .insert({
          workspace_id: workspaceId,
          name: trimmed,
          color: newTagColor,
        })
        .select()
        .single();

      if (error) throw error;

      setWorkspaceTags((prev) =>
        prev
          .map((t) => (t.id === tempId ? data : t))
          .sort((a, b) => a.name.localeCompare(b.name, "pt-BR")),
      );
      toast({
        title: "Tag criada",
        description: `Tag "${data.name}" criada com sucesso.`,
      });
    } catch (error: any) {
      setWorkspaceTags((prev) => prev.filter((t) => t.id !== tempId));
      toast({
        title: "Erro ao criar tag",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleStartEditTag = (tag: LeadTag) => {
    setIsCreatingTag(false);
    setNewTagName("");
    setEditingTag({ ...tag });
  };

  const handleCancelEditTag = () => {
    setEditingTag(null);
  };

  const handleUpdateTag = async () => {
    if (!editingTag) return;

    const trimmed = editingTag.name.trim();
    if (!trimmed) {
      toast({
        title: "Nome inválido",
        description: "Informe um nome para a tag.",
        variant: "destructive",
      });
      return;
    }

    const duplicate = workspaceTags.find(
      (t) => t.id !== editingTag.id && t.name.toLowerCase() === trimmed.toLowerCase(),
    );
    if (duplicate) {
      toast({
        title: "Esta tag já existe",
        description: "Escolha outro nome para a tag.",
        variant: "destructive",
      });
      return;
    }

    const previousWorkspaceTags = workspaceTags;
    const previousTag = workspaceTags.find((t) => t.id === editingTag.id);
    const updatedTag: LeadTag = { ...editingTag, name: trimmed };

    setWorkspaceTags((prev) =>
      prev
        .map((t) => (t.id === updatedTag.id ? updatedTag : t))
        .sort((a, b) => a.name.localeCompare(b.name, "pt-BR")),
    );
    patchLeadTagInList(updatedTag.id, { name: updatedTag.name, color: updatedTag.color });
    setEditingTag(null);

    try {
      const { error } = await supabase
        .from("lead_tags")
        .update({ name: updatedTag.name, color: updatedTag.color })
        .eq("id", updatedTag.id);

      if (error) throw error;

      toast({
        title: "Tag atualizada",
        description: `Tag renomeada para "${updatedTag.name}".`,
      });
    } catch (error: any) {
      setWorkspaceTags(previousWorkspaceTags);
      if (previousTag) {
        patchLeadTagInList(updatedTag.id, { name: previousTag.name, color: previousTag.color });
      }
      setEditingTag(updatedTag);
      toast({
        title: "Erro ao atualizar tag",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleDeleteTag = async (tagId: string, tagName: string) => {
    if (editingTag?.id === tagId) {
      setEditingTag(null);
    }

    try {
      const { error } = await supabase.from("lead_tags").delete().eq("id", tagId);
      if (error) throw error;

      setWorkspaceTags((prev) => prev.filter((t) => t.id !== tagId));
      setSelectedTags((prev) => prev.filter((id) => id !== tagId));
      toast({
        title: "Tag excluída",
        description: `Tag "${tagName}" foi excluída.`,
      });
    } catch (error: any) {
      toast({
        title: "Erro ao excluir tag",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleOptimisticUpdate = (leadId: string, patch: { stage?: string; order_index?: number }) => {
    setLeads((prevLeads) =>
      prevLeads.map((lead: any) => {
        if (lead.id !== leadId) return lead;
        const next = { ...lead };
        if (patch.stage !== undefined) next.stage = patch.stage;
        if (patch.order_index !== undefined) next.order_index = patch.order_index;
        return next;
      }),
    );
  };

  const handleKanbanStageCountShift = useCallback((fromStage: string, toStage: string) => {
    if (!fromStage || !toStage || fromStage === toStage) return;
    setKanbanStageCounts((prev) => {
      const next = { ...prev };
      if (next[fromStage] != null) {
        next[fromStage] = Math.max(0, (next[fromStage] || 0) - 1);
      }
      next[toStage] = (next[toStage] || 0) + 1;
      return next;
    });
  }, []);

  const handleExportLeads = () => {
    setIsExportDialogOpen(true);
  };

  const fetchStages = async () => {
    try {
      let query = supabase
        .from("lead_stages")
        .select("*")
        .eq("workspace_id", workspaceId)
        .order("order_index", { ascending: true });

      if (activeFunnelId) {
        query = query.eq("funnel_id", activeFunnelId);
      }

      const { data, error } = await query;
      if (error) throw error;
      setStages(data || []);
    } catch (error: any) {
      console.error("Error fetching stages:", error);
    }
  };

  const handleCreateLead = () => {
    setSelectedLead(null);
    setIsDialogOpen(true);
  };

  const handleOpenLeadQuickChat = (lead: Lead) => {
    setQuickChatLead(lead);
  };

  const handleEditLead = (lead: Lead) => {
    setSelectedLead(lead);
    setIsDetailDrawerOpen(true);
  };

  const handleDialogClose = (shouldRefresh: boolean) => {
    setIsDialogOpen(false);
    setSelectedLead(null);
    if (shouldRefresh) {
      fetchLeads();
    }
  };

  const groupedLeads = filteredLeads.reduce(
    (acc, lead) => {
      if (!acc[lead.stage]) {
        acc[lead.stage] = [];
      }
      acc[lead.stage].push(lead);
      return acc;
    },
    {} as Record<string, Lead[]>,
  );

  const crmStatCards = [
    {
      title: "Total de Leads",
      value: displayLeadTotal,
      description: hasActiveListFilters ? "Leads no filtro atual" : "Leads cadastrados",
      icon: Users,
      gradient: "from-blue-500 to-cyan-500",
    },
    {
      title: "Esta Semana",
      value: crmStats?.newLast7d ?? 0,
      description: "Novos leads",
      icon: TrendingUp,
      gradient: "from-emerald-500 to-teal-500",
    },
    {
      title: "Com Agendamento",
      value: crmStats?.withAppointment ?? 0,
      description: "Leads agendados",
      icon: Calendar,
      gradient: "from-violet-500 to-purple-500",
    },
    {
      title: "Média por Estágio",
      value: crmStats?.avgPerStage ?? 0,
      description: "Distribuição média",
      icon: Users,
      gradient: "from-orange-500 to-pink-500",
    },
  ];

  const isKanbanView = viewMode === "kanban";

  if (initialLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="w-16 h-16 rounded-full bg-gradient-to-r from-primary to-primary/50 animate-pulse mx-auto mb-4"></div>
            <Sparkles className="w-6 h-6 text-primary absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
          </div>
          <p className="text-muted-foreground">Carregando leads...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full flex flex-col overflow-hidden">
      {/* Header fixo - Layout minimalista */}
      <div className="flex-shrink-0 px-4 sm:px-6 py-4 border-b border-border bg-background">
        {/* Primeira linha: Título + Botões de visualização */}
        <div className="flex items-center justify-between gap-4 mb-3">
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold text-foreground truncate">
              Leads | CRM
            </h1>
            <p className="text-muted-foreground text-xs sm:text-sm hidden sm:block">
              Gerencie seus leads e pipeline de vendas
            </p>
          </div>

          {/* Botões de Visualização */}
          <div className="flex items-center gap-1 border rounded-lg p-1 bg-muted/30">
            <Button
              variant={viewMode === "kanban" ? "secondary" : "ghost"}
              size="sm"
              onClick={() => setViewMode("kanban")}
              className="h-7 px-2.5"
            >
              <Columns className="w-4 h-4" />
              <span className="hidden lg:inline ml-1.5">Kanban</span>
            </Button>
            <Button
              variant={viewMode === "grid" ? "secondary" : "ghost"}
              size="sm"
              onClick={() => setViewMode("grid")}
              className="h-7 px-2.5"
            >
              <LayoutGrid className="w-4 h-4" />
              <span className="hidden lg:inline ml-1.5">Grade</span>
            </Button>
            <Button
              variant={viewMode === "list" ? "secondary" : "ghost"}
              size="sm"
              onClick={() => setViewMode("list")}
              className="h-7 px-2.5"
            >
              <List className="w-4 h-4" />
              <span className="hidden lg:inline ml-1.5">Lista</span>
            </Button>
          </div>
        </div>

        {/* Segunda linha: Ações */}
        <div className="flex items-center gap-2 min-w-0">
          {/* Toggle de Modo de Dados + Seletor condicional */}
          <div className="flex items-center gap-2 min-w-0 flex-1">
            {/* Toggle Funis/Grupos */}
            <div className="flex items-center gap-0.5 border rounded-lg p-0.5 bg-muted/30 shrink-0">
              <Button
                variant={dataMode === "funnels" ? "secondary" : "ghost"}
                size="sm"
                onClick={() => setDataMode("funnels")}
                className="h-7 px-2"
              >
                <GitBranch className="w-4 h-4" />
                <span className="hidden sm:inline ml-1.5">Funis</span>
              </Button>
              <Button
                variant={dataMode === "groups" ? "secondary" : "ghost"}
                size="sm"
                onClick={() => setDataMode("groups")}
                className="h-7 px-2"
              >
                <Building2 className="w-4 h-4" />
                <span className="hidden sm:inline ml-1.5">Grupos</span>
              </Button>
            </div>

            {/* Seletor condicional baseado no modo */}
            <div className="min-w-0">
              {dataMode === "groups" ? (
                <GroupDropdownMenu
                  groups={groups}
                  selectedGroupId={selectedGroupIdForView}
                  onSelectGroup={setSelectedGroupIdForView}
                  onOpenManager={() => setIsGroupsManagerOpen(true)}
                />
              ) : (
                <FunnelDropdownMenu
                  funnels={funnels}
                  activeFunnelId={activeFunnelId}
                  onSelectFunnel={setActiveFunnelId}
                  onOpenManager={() => setIsFunnelManagerOpen(true)}
                  onOpenMoveLeads={() => setIsMoveLeadsOpen(true)}
                  loading={funnelsLoading}
                  integrationById={integrationById}
                />
              )}
            </div>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant={statsOpen ? "secondary" : "ghost"}
                    size="icon"
                    className="h-7 w-7 shrink-0"
                    onClick={() => setStatsOpen((open) => !open)}
                    aria-label="Estatísticas"
                    aria-pressed={statsOpen}
                  >
                    <BarChart3 className="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Estatísticas</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          {/* Botões de ação à direita */}
          <div className="flex items-center gap-1 sm:gap-2 shrink-0">
            <Button
              onClick={() => setIsCsvImportOpen(true)}
              variant="ghost"
              size="sm"
              className="h-8 shrink-0 text-muted-foreground"
            >
              <FileSpreadsheet className="w-4 h-4" />
              <span className="hidden md:inline ml-1.5">Importar</span>
            </Button>
            <Button
              onClick={handleExportLeads}
              variant="ghost"
              size="sm"
              className="h-8 shrink-0 text-muted-foreground"
            >
              <Download className="w-4 h-4" />
              <span className="hidden md:inline ml-1.5">Exportar</span>
            </Button>
            {dataMode === "groups" && selectedGroupIdForView ? (
              <Button
                onClick={() => setIsGroupStageManagerOpen(true)}
                variant="ghost"
                size="sm"
                className="h-8 shrink-0 text-muted-foreground"
              >
                <Settings className="w-4 h-4" />
                <span className="hidden md:inline ml-1.5">Colunas</span>
              </Button>
            ) : dataMode === "funnels" && canManageFunnels ? (
              <Button
                onClick={() => setIsStageManagerOpen(true)}
                variant="ghost"
                size="sm"
                className="h-8 shrink-0 text-muted-foreground"
              >
                <Settings className="w-4 h-4" />
                <span className="hidden md:inline ml-1.5">Estágios</span>
              </Button>
            ) : null}
            <div className="w-px h-5 bg-border mx-1 hidden sm:block" />

            <Button onClick={handleCreateLead} size="sm" className="h-8 shrink-0">
              <Plus className="w-4 h-4" />
              <span className="hidden lg:inline ml-1.5">Novo Lead</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Conteúdo com scroll */}
      <div
        className={cn(
          "flex-1 w-full px-4 sm:px-6 py-4",
          isKanbanView
            ? "flex flex-col min-h-0 overflow-hidden gap-4"
            : "overflow-y-auto overflow-x-hidden space-y-4",
        )}
      >
        <Collapsible open={statsOpen} onOpenChange={setStatsOpen} className={cn(isKanbanView && "shrink-0")}>
          <CollapsibleContent className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {crmStatCards.map((stat, index) => (
                <Card key={index} className="group hover:shadow-lg transition-all duration-300 border overflow-hidden">
                  <CardContent className="p-4 relative">
                    <div className="flex items-start justify-between mb-3">
                      <div
                        className={`w-10 h-10 rounded-lg bg-gradient-to-br ${stat.gradient} flex items-center justify-center shadow-lg`}
                      >
                        <stat.icon className="w-5 h-5 text-white" />
                      </div>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">{stat.title}</p>
                      {crmStatsLoading ? (
                        <Skeleton className="h-8 w-16 mt-1 rounded-md" />
                      ) : (
                        <p className="text-2xl font-bold bg-gradient-to-br from-foreground to-foreground/70 bg-clip-text text-transparent">
                          {stat.value}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{stat.description}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CollapsibleContent>
        </Collapsible>

        <div
          className={cn(
            "flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-between",
            isKanbanView && "shrink-0",
          )}
        >
          <div className="flex items-center gap-2 flex-1 w-full">
            {/* Selection mode toggle */}
            <Button
              variant={isSelectionMode ? "secondary" : "outline"}
              size="icon"
              className="h-10 w-10 shrink-0"
              onClick={() => {
                if (isSelectionMode) {
                  exitSelectionMode();
                } else {
                  setIsSelectionMode(true);
                }
              }}
              title={isSelectionMode ? "Sair do modo seleção" : "Selecionar leads"}
            >
              <CheckSquare className="w-4 h-4" />
            </Button>

            {isSelectionMode && (
              <Checkbox
                checked={filteredLeads.length > 0 && selectedLeadIds.size === filteredLeads.length}
                onCheckedChange={toggleSelectAll}
                className="shrink-0"
              />
            )}

            <div ref={searchDropdownRef} className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4 z-10" />
              <Input
                placeholder="Buscar por nome, email ou telefone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onBlur={() => {
                  setTimeout(() => {
                    setSearchDropdownOpen(false);
                  }, 200);
                }}
                onFocus={() => {
                  if (searchTerm.trim() && (searchPreviewLeads.length > 0 || searchPreviewLoading)) {
                    setSearchDropdownOpen(true);
                  }
                }}
                className="pl-10 w-full h-10 text-sm"
              />
              {searchDropdownOpen && searchTerm.trim() && (
                <div className="absolute top-full left-0 right-0 mt-1 z-50 rounded-md border bg-popover shadow-md max-h-60 overflow-auto">
                  {searchPreviewLoading ? (
                    <div className="px-3 py-4 text-sm text-muted-foreground text-center">
                      Buscando...
                    </div>
                  ) : searchPreviewLeads.length === 0 ? (
                    <div className="px-3 py-4 text-sm text-muted-foreground text-center">
                      Nenhum lead encontrado
                    </div>
                  ) : (
                    <ul className="p-1">
                      {searchPreviewLeads.map((lead) => (
                        <li key={lead.id}>
                          <button
                            type="button"
                            className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm rounded-md hover:bg-muted focus:bg-muted focus:outline-none"
                            onClick={() => {
                              handleEditLead(lead);
                              setSearchDropdownOpen(false);
                            }}
                          >
                            <Search className="w-4 h-4 text-muted-foreground shrink-0" />
                            <div className="min-w-0 flex-1">
                              <div className="font-medium truncate">{lead.name || "Sem nome"}</div>
                              {(lead.email || lead.phone) && (
                                <div className="text-xs text-muted-foreground truncate">
                                  {[lead.email, lead.phone].filter(Boolean).join(" · ")}
                                </div>
                              )}
                            </div>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-2 items-center w-full sm:w-auto">
            <Filter className="w-4 h-4 text-muted-foreground shrink-0 hidden sm:block" />
            <Select value={selectedStage} onValueChange={setSelectedStage}>
              <SelectTrigger className="w-full sm:w-[140px] h-10 text-sm">
                <SelectValue placeholder="Filtrar por estágio" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os estágios</SelectItem>
                {stages.map((stage) => (
                  <SelectItem key={stage.id} value={stage.slug}>
                    {stage.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Popover open={tagFilterOpen} onOpenChange={setTagFilterOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="h-10 gap-2">
                  <Tags className="w-4 h-4" />
                  {selectedTags.length > 0 ? (
                    <Badge variant="secondary" className="px-1.5 py-0.5 text-xs">
                      {selectedTags.length}
                    </Badge>
                  ) : (
                    <span className="hidden sm:inline text-sm">Tags</span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-64 p-2" align="end">
                <div className="space-y-2">
                  <div className="flex items-center justify-between px-2 py-1">
                    <span className="text-sm font-medium">Tags</span>
                    {selectedTags.length > 0 && (
                      <Button variant="ghost" size="sm" onClick={clearTagFilter} className="h-6 px-2 text-xs">
                        Limpar filtro
                      </Button>
                    )}
                  </div>
                  <div className="border-t pt-2">
                    {workspaceTags.length === 0 && !isCreatingTag ? (
                      <p className="text-xs text-muted-foreground text-center py-2">Nenhuma tag criada</p>
                    ) : (
                      <div className="space-y-1 max-h-48 overflow-y-auto">
                        {workspaceTags.map((tag) => (
                          <div key={tag.id} className="rounded-md hover:bg-muted group">
                            {editingTag?.id === tag.id ? (
                              <div className="space-y-2 p-2 bg-muted/30">
                                <Input
                                  placeholder="Nome da tag"
                                  value={editingTag.name}
                                  onChange={(e) => setEditingTag({ ...editingTag, name: e.target.value })}
                                  className="h-8 text-sm"
                                  autoFocus
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") handleUpdateTag();
                                    if (e.key === "Escape") handleCancelEditTag();
                                  }}
                                />
                                <div className="flex flex-wrap gap-1.5">
                                  {TAG_COLORS.map((color) => (
                                    <button
                                      key={color.value}
                                      type="button"
                                      onClick={() => setEditingTag({ ...editingTag, color: color.value })}
                                      className={cn(
                                        "w-5 h-5 rounded-full transition-all",
                                        color.value,
                                        editingTag.color === color.value
                                          ? "ring-2 ring-offset-2 ring-primary"
                                          : "hover:scale-110",
                                      )}
                                      title={color.name}
                                    />
                                  ))}
                                </div>
                                <div className="flex gap-2">
                                  <Button
                                    type="button"
                                    size="sm"
                                    onClick={handleUpdateTag}
                                    disabled={!editingTag.name.trim()}
                                    className="flex-1 h-7 text-xs"
                                  >
                                    <Check className="w-3 h-3 mr-1" />
                                    Salvar
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={handleCancelEditTag}
                                    className="h-7 text-xs"
                                  >
                                    Cancelar
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2 px-2 py-1.5">
                                <Checkbox
                                  checked={selectedTags.includes(tag.id)}
                                  onCheckedChange={() => handleTagToggle(tag.id)}
                                />
                                <div
                                  className="flex items-center gap-2 flex-1 cursor-pointer min-w-0"
                                  onClick={() => handleTagToggle(tag.id)}
                                >
                                  <div className={`w-3 h-3 rounded-full shrink-0 ${tag.color}`} />
                                  <span className="text-sm flex-1 truncate">{tag.name}</span>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleStartEditTag(tag);
                                  }}
                                  title="Editar tag"
                                >
                                  <Pencil className="w-3 h-3" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteTag(tag.id, tag.name);
                                  }}
                                  title="Excluir tag"
                                >
                                  <Trash2 className="w-3 h-3 text-destructive" />
                                </Button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Inline tag creation */}
                  <div className="border-t pt-2">
                    {isCreatingTag ? (
                      <div className="space-y-2 p-2 bg-muted/30 rounded-md">
                        <Input
                          placeholder="Nome da tag"
                          value={newTagName}
                          onChange={(e) => setNewTagName(e.target.value)}
                          className="h-8 text-sm"
                          autoFocus
                        />
                        <div className="flex flex-wrap gap-1.5">
                          {TAG_COLORS.map((color) => (
                            <button
                              key={color.value}
                              type="button"
                              onClick={() => setNewTagColor(color.value)}
                              className={cn(
                                "w-5 h-5 rounded-full transition-all",
                                color.value,
                                newTagColor === color.value ? "ring-2 ring-offset-2 ring-primary" : "hover:scale-110",
                              )}
                              title={color.name}
                            />
                          ))}
                        </div>
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            size="sm"
                            onClick={handleCreateTagInline}
                            disabled={!newTagName.trim()}
                            className="flex-1 h-7 text-xs"
                          >
                            Criar
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setIsCreatingTag(false);
                              setNewTagName("");
                            }}
                            className="h-7 text-xs"
                          >
                            Cancelar
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setEditingTag(null);
                          setIsCreatingTag(true);
                        }}
                        className="w-full justify-start h-8 text-sm"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Nova Tag
                      </Button>
                    )}
                  </div>
                </div>
              </PopoverContent>
            </Popover>

            {/* Dropdown de Ordenação */}
            <Select value={sortOption} onValueChange={setSortOption}>
              <SelectTrigger className="w-full sm:w-[160px] h-10 text-sm">
                <ArrowUpDown className="w-4 h-4 mr-1.5 shrink-0" />
                <SelectValue placeholder="Ordenar" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="recent">Mais recentes</SelectItem>
                <SelectItem value="oldest">Mais antigos</SelectItem>
                <SelectItem value="az">A → Z</SelectItem>
                <SelectItem value="za">Z → A</SelectItem>
                <SelectItem value="stage">Estágio</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {leads.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16">
              <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mb-6">
                <Users className="w-10 h-10 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Nenhum lead cadastrado</h3>
              <p className="text-muted-foreground text-center mb-6 max-w-md">
                Comece a gerenciar seus leads criando o primeiro. Você pode importar leads ou criar manualmente.
              </p>
              <Button onClick={handleCreateLead} size="lg">
                <Plus className="w-4 h-4 mr-2" />
                Criar Primeiro Lead
              </Button>
            </CardContent>
          </Card>
        ) : filteredLeads.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16">
              <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mb-6">
                <Search className="w-10 h-10 text-muted-foreground" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Nenhum resultado encontrado</h3>
              <p className="text-muted-foreground text-center mb-6 max-w-md">
                Tente ajustar seus filtros de busca para encontrar o que está procurando.
              </p>
              <Button
                variant="outline"
                onClick={() => {
                  setSearchTerm("");
                  setSelectedStage("all");
                  setSelectedTags([]);
                }}
              >
                Limpar Filtros
              </Button>
            </CardContent>
          </Card>
        ) : dataMode === "groups" ? (
          // Groups Mode - renderização especial
          !selectedGroupIdForView ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-16">
                <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mb-6">
                  <Building2 className="w-10 h-10 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Selecione um grupo</h3>
                <p className="text-muted-foreground text-center mb-6 max-w-md">
                  Use o seletor acima para escolher um grupo e visualizar seus leads.
                </p>
                {groups.length === 0 && (
                  <Button onClick={() => setIsGroupsManagerOpen(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Criar Primeiro Grupo
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : viewMode === "kanban" ? (
            <div className="flex-1 min-h-0 overflow-hidden">
              <GroupsKanban
                groupId={selectedGroupIdForView}
                workspaceId={workspaceId}
                onLeadClick={handleEditLead}
              />
            </div>
          ) : viewMode === "grid" ? (
            // Grid view for groups - placeholder
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-16">
                <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mb-6">
                  <LayoutGrid className="w-10 h-10 text-muted-foreground" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Visualização em Grade</h3>
                <p className="text-muted-foreground text-center mb-6 max-w-md">
                  A visualização em grade para grupos está disponível apenas no modo Kanban por enquanto.
                </p>
                <Button onClick={() => setViewMode("kanban")}>
                  <Columns className="w-4 h-4 mr-2" />
                  Usar Kanban
                </Button>
              </CardContent>
            </Card>
          ) : (
            // List view for groups - placeholder
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-16">
                <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mb-6">
                  <List className="w-10 h-10 text-muted-foreground" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Visualização em Lista</h3>
                <p className="text-muted-foreground text-center mb-6 max-w-md">
                  A visualização em lista para grupos está disponível apenas no modo Kanban por enquanto.
                </p>
                <Button onClick={() => setViewMode("kanban")}>
                  <Columns className="w-4 h-4 mr-2" />
                  Usar Kanban
                </Button>
              </CardContent>
            </Card>
          )
        ) : (
          <>
            {/* Kanban View for Funnels */}
            {viewMode === "kanban" && (
              <div className="flex-1 min-h-0 overflow-hidden">
                <LeadsKanban
                  workspaceId={workspaceId}
                  leads={filteredLeads}
                  sortOption={sortOption}
                  onLeadMoveCommitted={handleKanbanStageCountShift}
                  onOptimisticUpdate={handleOptimisticUpdate}
                  onLeadClick={handleEditLead}
                  onOpenLeadChat={handleOpenLeadQuickChat}
                  selectionMode={isSelectionMode}
                  selectedIds={selectedLeadIds}
                  onToggleSelect={toggleSelectLead}
                  onToggleSelectLeadsInStage={toggleSelectLeadsInStage}
                  fullySelectedStageSlugs={fullySelectedStageSlugs}
                  selectingStageSlug={selectingStageSlug}
                  primaryGroupsMap={primaryGroupsMap}
                  assignedMembersMap={assignedMembersMap}
                  closedLeadsSet={closedLeadsSet}
                  unreadCountsMap={unreadCountsMap}
                  unreadLastMessageAtMap={unreadLastMessageAtMap}
                  activeFunnelId={activeFunnelId}
                  isIntegrationFunnel={isIntegrationFunnel}
                  stageCounts={kanbanDisplayCounts}
                  onLoadMoreStage={handleLoadMoreKanbanStage}
                  scrollToStageSlug={scrollToStageSlug}
                  onScrollToStageConsumed={() => setScrollToStageSlug(null)}
                />
              </div>
            )}

            {/* Grid View */}
            {viewMode === "grid" && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pb-4">
                {stages.map((stage) => {
                  const stageLeads = groupedLeads[stage.slug] || [];
                  return (
                    <Card key={stage.id} className="hover:shadow-lg transition-shadow">
                      <CardHeader className="pb-3 border-b">
                        <CardTitle className="text-base font-semibold flex items-center gap-2">
                          <div className={`w-3 h-3 rounded-full ${stage.color}`} />
                          {stage.name}
                          <Badge variant="secondary" className="ml-auto">
                            {stageLeads.length}
                          </Badge>
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3 pt-4">
                        {stageLeads.map((lead) => (
                          <div
                            key={lead.id}
                            onClick={() => isSelectionMode ? toggleSelectLead(lead.id) : handleEditLead(lead)}
                            className={cn(
                              "p-4 border rounded-lg cursor-pointer hover:bg-muted/50 hover:border-primary/50 transition-all group",
                              isSelectionMode && selectedLeadIds.has(lead.id) && "ring-2 ring-primary bg-primary/5"
                            )}
                          >
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex items-center gap-2">
                                {isSelectionMode && (
                                  <Checkbox
                                    checked={selectedLeadIds.has(lead.id)}
                                    onCheckedChange={() => toggleSelectLead(lead.id)}
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                )}
                                <p className="font-semibold group-hover:text-primary transition-colors">{lead.name}</p>
                              </div>
                              {lead.current_appointment_date && (
                                <Badge variant="outline" className="text-xs shrink-0 ml-2">
                                  <Calendar className="w-3 h-3 mr-1" />
                                  {new Date(lead.current_appointment_date).toLocaleDateString("pt-BR", {
                                    day: "2-digit",
                                    month: "2-digit",
                                  })}
                                </Badge>
                              )}
                            </div>
                            <div className="space-y-1">
                              {lead.email && <p className="text-xs text-muted-foreground truncate">{lead.email}</p>}
                              {lead.phone && <p className="text-xs text-muted-foreground">{lead.phone}</p>}
                            </div>
                            <p className="text-xs text-muted-foreground mt-2">
                              Criado em {new Date(lead.created_at).toLocaleDateString("pt-BR")}
                            </p>
                          </div>
                        ))}
                        {stageLeads.length === 0 && (
                          <div className="text-center py-8">
                            <p className="text-sm text-muted-foreground">Nenhum lead neste estágio</p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}

            {/* List View */}
            {viewMode === "list" && (
              <div className="space-y-4">
                <Card>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        {isSelectionMode && <TableHead className="w-10" />}
                        <TableHead>Nome</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Telefone</TableHead>
                        <TableHead>Estágio</TableHead>
                        <TableHead>Tags</TableHead>
                        <TableHead>Criado em</TableHead>
                        <TableHead className="text-right">Ações</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(() => {
                        const totalFilteredLeads = filteredLeads.length;
                        const totalPages = Math.ceil(totalFilteredLeads / LEADS_PER_PAGE);
                        const startIndex = (currentPage - 1) * LEADS_PER_PAGE;
                        const endIndex = startIndex + LEADS_PER_PAGE;
                        const paginatedLeads = filteredLeads.slice(startIndex, endIndex);

                        const getStageColor = (stageSlug: string | null) => {
                          const stage = stages.find((s) => s.slug === stageSlug);
                          return stage?.color || "bg-gray-500";
                        };

                        const getStageName = (stageSlug: string | null) => {
                          const stage = stages.find((s) => s.slug === stageSlug);
                          return stage?.name || stageSlug || "Sem estágio";
                        };

                        return paginatedLeads.map((lead) => (
                          <TableRow
                            key={lead.id}
                            className={cn("cursor-pointer hover:bg-muted/50", isSelectionMode && selectedLeadIds.has(lead.id) && "bg-primary/5")}
                            onClick={() => isSelectionMode ? toggleSelectLead(lead.id) : handleEditLead(lead)}
                          >
                            {isSelectionMode && (
                              <TableCell onClick={(e) => e.stopPropagation()}>
                                <Checkbox
                                  checked={selectedLeadIds.has(lead.id)}
                                  onCheckedChange={() => toggleSelectLead(lead.id)}
                                />
                              </TableCell>
                            )}
                            <TableCell className="font-medium">{lead.name}</TableCell>
                            <TableCell>{lead.email || "-"}</TableCell>
                            <TableCell>{lead.phone || "-"}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className="flex items-center gap-1.5 w-fit">
                                <div className={`w-2 h-2 rounded-full ${getStageColor(lead.stage)}`} />
                                {getStageName(lead.stage)}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-wrap gap-1">
                                {lead.tags?.slice(0, 2).map((tag) => (
                                  <Badge key={tag.id} variant="secondary" className="text-xs">
                                    {tag.name}
                                  </Badge>
                                ))}
                                {(lead.tags?.length || 0) > 2 && (
                                  <Badge variant="secondary" className="text-xs">
                                    +{lead.tags!.length - 2}
                                  </Badge>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {new Date(lead.created_at).toLocaleDateString("pt-BR")}
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditLead(lead);
                                }}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ));
                      })()}
                    </TableBody>
                  </Table>
                </Card>

                {/* Paginação */}
                {(() => {
                  const totalFilteredLeads = filteredLeads.length;
                  const totalPages = Math.ceil(totalFilteredLeads / LEADS_PER_PAGE);
                  const startIndex = (currentPage - 1) * LEADS_PER_PAGE;
                  const endIndex = Math.min(startIndex + LEADS_PER_PAGE, totalFilteredLeads);

                  if (totalPages <= 1) return null;

                  const getVisiblePages = () => {
                    const pages: (number | "ellipsis")[] = [];
                    if (totalPages <= 7) {
                      for (let i = 1; i <= totalPages; i++) pages.push(i);
                    } else {
                      pages.push(1);
                      if (currentPage > 3) pages.push("ellipsis");
                      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
                        pages.push(i);
                      }
                      if (currentPage < totalPages - 2) pages.push("ellipsis");
                      pages.push(totalPages);
                    }
                    return pages;
                  };

                  return (
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                      <p className="text-sm text-muted-foreground">
                        {totalFilteredLeads === 0
                          ? "Nenhum lead encontrado"
                          : (() => {
                              const loadedHint =
                                !hasActiveListFilters &&
                                totalFilteredLeads < displayLeadTotal
                                  ? ` (${totalFilteredLeads} carregados)`
                                  : "";
                              return `Mostrando ${startIndex + 1}-${endIndex} de ${displayLeadTotal} leads${loadedHint}`;
                            })()}
                      </p>
                      <Pagination>
                        <PaginationContent>
                          <PaginationItem>
                            <PaginationPrevious
                              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                              className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                            />
                          </PaginationItem>
                          {getVisiblePages().map((page, idx) => (
                            <PaginationItem key={idx}>
                              {page === "ellipsis" ? (
                                <span className="px-2 text-muted-foreground">...</span>
                              ) : (
                                <PaginationLink
                                  onClick={() => setCurrentPage(page)}
                                  isActive={currentPage === page}
                                  className="cursor-pointer"
                                >
                                  {page}
                                </PaginationLink>
                              )}
                            </PaginationItem>
                          ))}
                          <PaginationItem>
                            <PaginationNext
                              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                              className={
                                currentPage === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"
                              }
                            />
                          </PaginationItem>
                        </PaginationContent>
                      </Pagination>
                    </div>
                  );
                })()}
              </div>
            )}

            {serverHasMore && (
              <div className="flex justify-center pb-6">
                <Button
                  variant="outline"
                  onClick={() => fetchLeads(true, { loadMore: true })}
                  disabled={serverLoadingMore}
                >
                  {serverLoadingMore ? "Carregando..." : "Carregar mais leads"}
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {isSelectionMode && selectedLeadIds.size > 0 && (
        <BulkActionsBar
          selectedCount={selectedLeadIds.size}
          selectedIds={selectedLeadIds}
          stages={stages}
          funnels={funnels}
          workspaceId={workspaceId}
          activeFunnelId={activeFunnelId}
          workspaceTags={workspaceTags}
          onComplete={handleBulkComplete}
          onCancel={exitSelectionMode}
          integrationById={integrationById}
        />
      )}

      <LeadDialog
        open={isDialogOpen}
        onOpenChange={handleDialogClose}
        lead={selectedLead}
        workspaceId={workspaceId}
        funnelId={activeFunnelId ?? null}
        workspaceTags={workspaceTags}
        onLeadCreated={(leadId, leadName) => {
          setNewLeadForDeal({ id: leadId, name: leadName });
          toast({
            title: "Lead criado",
            description: "Deseja criar uma negociação para este lead?",
            action: (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsDealDialogOpen(true)}
              >
                Criar Negociação
              </Button>
            ),
          });
        }}
        onDuplicatePhone={async (existing) => {
          const { data: full } = await supabase
            .from("leads")
            .select("*")
            .eq("id", existing.id)
            .maybeSingle();
          if (full) {
            setSelectedLead(full as any);
            setIsDetailDrawerOpen(true);
          }
        }}
      />

      <LeadDetailDrawer
        open={isDetailDrawerOpen}
        onOpenChange={setIsDetailDrawerOpen}
        lead={selectedLead}
        workspaceId={workspaceId}
        activeFunnelId={activeFunnelId}
        onUpdate={fetchLeads}
      />

      <LeadQuickChatSheet
        open={quickChatLead !== null}
        onOpenChange={(open) => {
          if (!open) setQuickChatLead(null);
        }}
        lead={quickChatLead}
        workspaceId={workspaceId}
      />

      <StageManagerDialog
        open={isStageManagerOpen}
        onOpenChange={setIsStageManagerOpen}
        workspaceId={workspaceId}
        funnelId={activeFunnelId ?? null}
        canManage={canManageFunnels}
        onStagesUpdate={() => {
          fetchStages();
          fetchLeads();
        }}
      />

      <LeadCsvImportDialog
        open={isCsvImportOpen}
        onOpenChange={setIsCsvImportOpen}
        workspaceId={workspaceId}
        funnelId={activeFunnelId ?? null}
        onImportComplete={() => {
          fetchLeads();
          fetchStages();
        }}
      />

      <FunnelManagerDialog
        open={isFunnelManagerOpen}
        onOpenChange={setIsFunnelManagerOpen}
        funnels={funnels}
        onCreateFunnel={createFunnel}
        onUpdateFunnel={updateFunnel}
        onDeleteFunnel={deleteFunnel}
        onSetDefault={setDefaultFunnel}
        onReorderFunnels={reorderFunnels}
        canManage={canManageFunnels}
        integrationById={integrationById}
      />


      <MoveLeadsDialog
        open={isMoveLeadsOpen}
        onOpenChange={setIsMoveLeadsOpen}
        funnels={funnels}
        workspaceId={workspaceId}
        onComplete={() => {
          if (viewMode === "kanban") fetchKanbanLeads(true);
          else fetchLeads();
        }}
        integrationById={integrationById}
      />

      <ContactGroupsManager
        open={isGroupsManagerOpen}
        onOpenChange={setIsGroupsManagerOpen}
        workspaceId={workspaceId}
      />

      <GroupStageManagerDialog
        open={isGroupStageManagerOpen}
        onOpenChange={setIsGroupStageManagerOpen}
        groupId={selectedGroupIdForView}
        groupName={groups.find((g) => g.id === selectedGroupIdForView)?.name}
      />

      {newLeadForDeal && (
        <DealDialog
          open={isDealDialogOpen}
          onOpenChange={(open) => {
            setIsDealDialogOpen(open);
            if (!open) setNewLeadForDeal(null);
          }}
          workspaceId={workspaceId}
          stages={dealStages}
          memberId={memberId}
          onCreated={() => {}}
          initialLeadId={newLeadForDeal.id}
          initialLeadName={newLeadForDeal.name}
          lockLead
        />
      )}

      <ExportLeadsDialog
        open={isExportDialogOpen}
        onOpenChange={setIsExportDialogOpen}
        workspaceId={workspaceId}
        dataMode={dataMode}
        activeFunnelId={activeFunnelId}
        activeFunnelName={activeFunnel?.name}
        isIntegrationFunnel={isIntegrationFunnel}
        isDefaultFunnel={isDefaultFunnel}
        selectedGroupId={selectedGroupIdForView}
        selectedGroupName={groups.find((g) => g.id === selectedGroupIdForView)?.name}
        stages={stages}
        debouncedSearchTerm={debouncedSearchTerm}
        selectedStage={selectedStage}
        selectedTags={selectedTags}
        sortOption={sortOption}
        selectedLeadIds={selectedLeadIds}
        displayLeadTotal={displayLeadTotal}
        loadedLeadCount={filteredLeads.length}
      />
    </div>
  );
}
